import AddIcon from '@mui/icons-material/Add';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
    Autocomplete,
    AutocompleteRenderGetTagProps,
    Box,
    Card,
    Checkbox,
    Chip,
    TextField,
    Typography,
} from '@mui/material';
import { DateRange, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { InsurancePeriod } from '../../../backendsdk';
import {
    TrackedButton as Button,
    TrackedDialog as Dialog,
    TrackedIconButton as IconButton,
} from '../../../components/TrackedComponents';
import useProfile from '../../../hooks/profile';
import { COVERAGE_TYPES } from './InsurancePeriods';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface PeriodModalProps {
    isLoading: boolean;
    periods: InsurancePeriod[];
    selectedPeriod?: InsurancePeriod;
    onSave: CallableFunction;
    onClose: CallableFunction;
}

const PeriodModal: React.FC<PeriodModalProps> = (props: PeriodModalProps) => {
    const initialStartDate = props.selectedPeriod ? dayjs(props.selectedPeriod.start_date) : null;
    const initialEndDate = props.selectedPeriod ? dayjs(props.selectedPeriod.end_date) : null;
    const [dateRange, setDateRange] = React.useState<DateRange<Dayjs>>([initialStartDate, initialEndDate]);
    const [coverageType, setCoverageType] = React.useState<string>(props.selectedPeriod?.coverage_type || '');
    const [carrier, setCarrier] = React.useState<string>(props.selectedPeriod?.carrier || '');
    const [policyNumber, setPolicyNumber] = React.useState<string>(props.selectedPeriod?.policy_number || '');
    const { t } = useTranslation();
    const { profile } = useProfile();

    const isDateRangeValid =
        dateRange[0] !== null &&
        dateRange[1] !== null &&
        dateRange[0].isValid() &&
        dateRange[1].isValid() &&
        dateRange[0].isBefore(dateRange[1]);

    const periodAlreadyExists = props.periods.some((period) => {
        return (
            period.id !== props.selectedPeriod?.id &&
            period.coverage_type === coverageType &&
            period.carrier === carrier &&
            period.policy_number === policyNumber &&
            ((dateRange[0]?.isSame(dayjs(period.start_date)) && dateRange[1]?.isSame(dayjs(period.end_date))) ||
                (dateRange[0]?.isAfter(dayjs(period.start_date)) && dateRange[0]?.isBefore(dayjs(period.end_date))) ||
                (dateRange[1]?.isAfter(dayjs(period.start_date)) && dateRange[1]?.isBefore(dayjs(period.end_date))))
        );
    });

    const isPeriodValid =
        isDateRangeValid && coverageType !== '' && carrier !== '' && policyNumber !== '' && !periodAlreadyExists;

    return (
        <Dialog
            id="period-modal"
            open={true}
            maxWidth="xs"
            fullWidth
            onClose={() => {
                if (!props.isLoading) {
                    props.onClose();
                }
            }}
        >
            <Box
                sx={{
                    backgroundColor: 'secondary.main',
                    top: 0,
                    p: 1,
                    zIndex: 2,
                    minHeight: 0,
                    flexShrink: 0,
                }}
            >
                <Box sx={{ position: 'absolute', display: 'flex', right: 0, top: 0, mt: 0.5, mr: 0.5 }}>
                    <IconButton id="btn-close" size="small" disabled={props.isLoading} onClick={() => props.onClose()}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {!!props.selectedPeriod ? <EditIcon /> : <AddIcon />}
                    <Typography sx={{ ml: 0.5 }}>
                        {!!props.selectedPeriod
                            ? t('content.loss_runs.edit_insurance_period')
                            : t('content.loss_runs.add_insurance_period')}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ width: '100%', p: 1 }}>
                <Card sx={{ display: 'flex', flexDirection: 'column', width: '100%', p: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateRangePicker
                            label={t('content.loss_runs.date_range')}
                            slots={{ field: SingleInputDateRangeField }}
                            format={profile.dateFormat}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    error: periodAlreadyExists,
                                    helperText: periodAlreadyExists ? t('content.loss_runs.already_exists') : '',
                                },
                            }}
                            value={dateRange}
                            onChange={(newValue) => setDateRange(newValue)}
                        />
                    </LocalizationProvider>
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        id="coverage-type-field"
                        size="small"
                        options={COVERAGE_TYPES[profile.customer.settings.country as 'us' | 'il']}
                        value={
                            !!coverageType
                                ? coverageType.split(',')
                                : //   .map((type) => t(`content.loss_runs.period_table.${type.toLocaleLowerCase()}`))
                                  []
                        }
                        renderTags={(value: Array<string>, getTagProps: AutocompleteRenderGetTagProps) => {
                            return value.map((coverageType) => (
                                <Chip
                                    size="small"
                                    label={
                                        <Typography fontSize={12} sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {t(`content.loss_runs.period_table.${coverageType.toLocaleLowerCase()}`)}
                                        </Typography>
                                    }
                                    {...getTagProps({ index: 0 })}
                                    sx={{ borderRadius: 1, maxWidth: '250px !important' }}
                                    onDelete={undefined}
                                    key={coverageType}
                                />
                            ));
                        }}
                        onChange={(_, newValue) => {
                            setCoverageType(newValue.sort((a, b) => a.localeCompare(b)).join(','));
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label={t('content.loss_runs.period_table.coverage_type')} />
                        )}
                        renderOption={(props, option, { selected }) => (
                            <li {...props} style={{ height: 40, paddingLeft: 0 }}>
                                <Checkbox
                                    id={`${option}-checkbox`}
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                />
                                {t(`content.loss_runs.period_table.${option.toLocaleLowerCase()}`)}
                            </li>
                        )}
                        sx={{ mt: 1.5 }}
                    />
                    <Box sx={{ display: 'flex', width: '100%', mt: 1.5 }}>
                        <TextField
                            id="carrier-field"
                            label={t('content.loss_runs.period_table.carrier')}
                            size="small"
                            value={carrier}
                            onChange={(e) => setCarrier(e.target.value)}
                            sx={{ mr: 1 }}
                        />
                        <TextField
                            id="policy-number-field"
                            inputProps={{ 'data-testid': 'policy-number-field' }}
                            label={t('content.loss_runs.period_table.policy_number')}
                            size="small"
                            value={policyNumber}
                            onChange={(e) => setPolicyNumber(e.target.value)}
                        />
                    </Box>
                </Card>
                <Box sx={{ display: 'flex', width: '100%', mt: 1 }}>
                    <Button
                        id="btn-cancel"
                        fullWidth
                        variant="contained"
                        color="primary"
                        disabled={props.isLoading}
                        onClick={() => props.onClose()}
                        sx={{ mr: 1 }}
                    >
                        {t('content.loss_runs.cancel')}
                    </Button>
                    <Button
                        id="btn-save"
                        data-testid="btn-save"
                        fullWidth
                        variant="contained"
                        color="secondary"
                        disabled={!isPeriodValid || props.isLoading}
                        onClick={() =>
                            props.onSave({
                                id: props.selectedPeriod?.id,
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                start_date: dateRange[0]!.format('YYYY-MM-DD'),
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                end_date: dateRange[1]!.format('YYYY-MM-DD'),
                                coverage_type: coverageType,
                                carrier,
                                policy_number: policyNumber,
                            })
                        }
                    >
                        {t('content.loss_runs.save')}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default PeriodModal;
